// ========================================
// SETTINGS MANAGER
// Gestionnaire des paramètres du site
// ========================================

import React, { useState, useEffect } from 'react';
import AdminLayout from '../../../components/admin/Layout/AdminLayout';
import useSiteSettings from '../../../hooks/useSiteSettings';
import { ImageUploader } from '../../../components/admin/Editor';
import {
  FiSave,
  FiGlobe,
  FiSearch,
  FiShare2,
  FiLoader,
  FiLink,
  FiImage
} from 'react-icons/fi';
import { 
  FaLinkedin, 
  FaTwitter, 
  FaFacebook, 
  FaInstagram, 
  FaYoutube 
} from 'react-icons/fa';
import './SettingsManager.css';

const SettingsManager = () => {
  const { settings, loading, saving, saveSettings } = useSiteSettings();
  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState(null);

  useEffect(() => {
    if (settings) {
      setFormData(settings);
    }
  }, [settings]);

  const tabs = [
    { id: 'general', label: 'Général', icon: FiGlobe },
    { id: 'seo', label: 'SEO', icon: FiSearch },
    { id: 'social', label: 'Réseaux sociaux', icon: FiShare2 }
  ];

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedFormData = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    await saveSettings(formData);
  };

  if (loading || !formData) {
    return (
      <AdminLayout>
        <div className="manager-loading">
          <FiLoader className="spinner" />
          <p>Chargement des paramètres...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="settings-manager">
        {/* Header */}
        <div className="manager-header">
          <div>
            <h1>Paramètres</h1>
            <p>Configuration générale de votre site</p>
          </div>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <FiLoader className="spinner" /> : <FiSave />}
            Sauvegarder tout
          </button>
        </div>

        {/* Tabs */}
        <div className="manager-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`manager-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="manager-content">

          {/* GÉNÉRAL */}
          {activeTab === 'general' && (
            <div className="manager-section">
              <div className="settings-grid">
                {/* Informations du site */}
                <div className="form-card">
                  <h3><FiGlobe /> Informations du site</h3>

                  <div className="form-group">
                    <label className="form-label">Nom du site</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.siteName || ''}
                      onChange={(e) => updateFormData('siteName', e.target.value)}
                      placeholder="Gustave KONATE"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Description du site</label>
                    <textarea
                      className="form-input form-textarea"
                      value={formData.siteDescription || ''}
                      onChange={(e) => updateFormData('siteDescription', e.target.value)}
                      placeholder="Une courte description de votre site"
                      rows={3}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email de contact</label>
                    <input
                      type="email"
                      className="form-input"
                      value={formData.email || ''}
                      onChange={(e) => updateFormData('email', e.target.value)}
                      placeholder="contact@example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Téléphones</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.phones?.join(', ') || ''}
                      onChange={(e) => updateFormData('phones', e.target.value.split(',').map(p => p.trim()))}
                      placeholder="+226 70 11 64 34, +226 76 96 57 05"
                    />
                    <span className="form-hint">Séparez les numéros par une virgule</span>
                  </div>
                </div>

                {/* Logo et Favicon */}
                <div className="form-card">
                  <h3><FiImage /> Logo & Favicon</h3>

                  <div className="form-group">
                    <label className="form-label">Logo du site</label>
                    <ImageUploader
                      value={formData.logo}
                      onChange={(url) => updateFormData('logo', url)}
                      folder="portfolio/brand"
                      placeholder="Logo principal"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Favicon</label>
                    <ImageUploader
                      value={formData.favicon}
                      onChange={(url) => updateFormData('favicon', url)}
                      folder="portfolio/brand"
                      placeholder="Favicon (32x32px)"
                    />
                    <span className="form-hint">Format carré recommandé (32x32 ou 64x64 pixels)</span>
                  </div>
                </div>

                {/* Footer */}
                <div className="form-card full-width">
                  <h3><FiLink /> Pied de page</h3>

                  <div className="form-group">
                    <label className="form-label">Texte de copyright</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.footer?.copyright || ''}
                      onChange={(e) => updateNestedFormData('footer', 'copyright', e.target.value)}
                      placeholder="© 2025 Gustave KONATE. Tous droits réservés."
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SEO */}
          {activeTab === 'seo' && (
            <div className="manager-section">
              <div className="section-header">
                <h2>Référencement (SEO)</h2>
                <p>Optimisez votre visibilité sur les moteurs de recherche</p>
              </div>

              <div className="form-card">
                <div className="form-group">
                  <label className="form-label">Titre par défaut</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.seo?.defaultTitle || ''}
                    onChange={(e) => updateNestedFormData('seo', 'defaultTitle', e.target.value)}
                    placeholder="Gustave KONATE | Communicant & Leader Associatif"
                  />
                  <span className="form-hint">Titre affiché dans l'onglet du navigateur</span>
                </div>

                <div className="form-group">
                  <label className="form-label">Description par défaut</label>
                  <textarea
                    className="form-input form-textarea"
                    value={formData.seo?.defaultDescription || ''}
                    onChange={(e) => updateNestedFormData('seo', 'defaultDescription', e.target.value)}
                    placeholder="Description affichée dans les résultats de recherche"
                    rows={3}
                  />
                  <span className="form-hint">Idéalement entre 150 et 160 caractères</span>
                </div>

                <div className="form-group">
                  <label className="form-label">ID Google Analytics</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.seo?.googleAnalytics || ''}
                    onChange={(e) => updateNestedFormData('seo', 'googleAnalytics', e.target.value)}
                    placeholder="G-XXXXXXXXXX"
                  />
                  <span className="form-hint">Optionnel - Pour suivre les statistiques de visite</span>
                </div>
              </div>

              {/* Aperçu Google */}
              <div className="seo-preview">
                <h4>Aperçu dans Google</h4>
                <div className="google-preview">
                  <div className="preview-title">
                    {formData.seo?.defaultTitle || 'Titre de votre site'}
                  </div>
                  <div className="preview-url">
                    https://votre-site.com
                  </div>
                  <div className="preview-description">
                    {formData.seo?.defaultDescription || 'Description de votre site qui apparaîtra dans les résultats de recherche...'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* RÉSEAUX SOCIAUX */}
          {activeTab === 'social' && (
            <div className="manager-section">
              <div className="section-header">
                <h2>Réseaux sociaux</h2>
                <p>Liens vers vos profils sur les réseaux sociaux</p>
              </div>

              <div className="form-card">
                <div className="social-links-grid">
                  <div className="social-input-group">
                    <div className="social-icon linkedin">
                      <FaLinkedin />
                    </div>
                    <input
                      type="url"
                      className="form-input"
                      value={formData.socialLinks?.linkedin || ''}
                      onChange={(e) => updateNestedFormData('socialLinks', 'linkedin', e.target.value)}
                      placeholder="https://linkedin.com/in/votre-profil"
                    />
                  </div>

                  <div className="social-input-group">
                    <div className="social-icon twitter">
                      <FaTwitter />
                    </div>
                    <input
                      type="url"
                      className="form-input"
                      value={formData.socialLinks?.twitter || ''}
                      onChange={(e) => updateNestedFormData('socialLinks', 'twitter', e.target.value)}
                      placeholder="https://twitter.com/votre-profil"
                    />
                  </div>

                  <div className="social-input-group">
                    <div className="social-icon facebook">
                      <FaFacebook />
                    </div>
                    <input
                      type="url"
                      className="form-input"
                      value={formData.socialLinks?.facebook || ''}
                      onChange={(e) => updateNestedFormData('socialLinks', 'facebook', e.target.value)}
                      placeholder="https://facebook.com/votre-page"
                    />
                  </div>

                  <div className="social-input-group">
                    <div className="social-icon instagram">
                      <FaInstagram />
                    </div>
                    <input
                      type="url"
                      className="form-input"
                      value={formData.socialLinks?.instagram || ''}
                      onChange={(e) => updateNestedFormData('socialLinks', 'instagram', e.target.value)}
                      placeholder="https://instagram.com/votre-profil"
                    />
                  </div>

                  <div className="social-input-group">
                    <div className="social-icon youtube">
                      <FaYoutube />
                    </div>
                    <input
                      type="url"
                      className="form-input"
                      value={formData.socialLinks?.youtube || ''}
                      onChange={(e) => updateNestedFormData('socialLinks', 'youtube', e.target.value)}
                      placeholder="https://youtube.com/c/votre-chaine"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </AdminLayout>
  );
};

export default SettingsManager;